import {
  LockOutlined,
  LoginOutlined,
  MailOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Alert, Button, Col, Divider, Form, Input, Result, Row } from 'antd';
import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useSearchParams } from 'react-router';

import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { login, loginReset } from '../reducers/user/reducer';
import {
  ELoginStatus,
  EMagicLinkStatus,
  ESessionStatus,
  MAGIC_LINK_QUERY_PARAM,
  SESSION_QUERY_PARAM,
} from '../reducers/user/types';
import { SPACING } from '../theme/tokens';

interface IForm {
  email: string;
  password: string;
}

const LOGIN_ERROR_MESSAGES: Record<ELoginStatus, string | null> = {
  [ELoginStatus.MAGIC_LINK_SENT]: null,
  [ELoginStatus.INVALID_CREDENTIALS]: 'Invalid email or password.',
  [ELoginStatus.COUNTRY_NOT_ALLOWED]:
    'Signing in from your current location is not allowed.',
  [ELoginStatus.RATE_LIMITED]:
    'Too many failed sign-in attempts. Try again later.',
};

function LoginContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const userIsLoggedIn = useSelector((s: RootState) => s.User.isLoggedIn);
  const userInProcess = useSelector((s: RootState) => s.User.inProcess);
  const userLoginStatus = useSelector((s: RootState) => s.User.loginStatus);
  const userEmail = useSelector((s: RootState) => s.User.email);
  const [searchParams] = useSearchParams();

  const [form] = Form.useForm<IForm>();

  if (userIsLoggedIn) {
    return <Navigate to="/home" replace />;
  }

  if (userLoginStatus === ELoginStatus.MAGIC_LINK_SENT) {
    return (
      <Result
        icon={<MailOutlined />}
        title="Check your email"
        subTitle={`We sent a sign-in link to ${userEmail}. The link is valid for 15 minutes and can be used only once.`}
        extra={
          <Button
            onClick={(): void => {
              dispatch(loginReset());
            }}
          >
            Back to sign in
          </Button>
        }
      />
    );
  }

  const magicLinkStatus: string | null = searchParams.get(
    MAGIC_LINK_QUERY_PARAM,
  );
  const loginErrorMessage: string | null =
    !userInProcess && userLoginStatus
      ? LOGIN_ERROR_MESSAGES[userLoginStatus]
      : null;

  return (
    <Form
      form={form}
      onFinish={({ email, password }: IForm): void => {
        dispatch(login(email, password));
      }}
    >
      <Row gutter={[SPACING.none, SPACING.xl]}>
        <Col span={8} offset={8} className="gutter-row">
          <Divider titlePlacement="start">Fill your information</Divider>
        </Col>

        {magicLinkStatus === EMagicLinkStatus.EXPIRED && (
          <Col span={8} offset={8} className="gutter-row">
            <Alert
              type="warning"
              showIcon
              title="The sign-in link is invalid or expired. Sign in again to get a new one."
            />
          </Col>
        )}

        {searchParams.get(SESSION_QUERY_PARAM) === ESessionStatus.EXPIRED && (
          <Col span={8} offset={8} className="gutter-row">
            <Alert
              type="warning"
              showIcon
              title="Your session has ended or was revoked. Sign in again."
            />
          </Col>
        )}

        {magicLinkStatus === EMagicLinkStatus.COUNTRY_NOT_ALLOWED && (
          <Col span={8} offset={8} className="gutter-row">
            <Alert
              type="error"
              showIcon
              title="Signing in from your current location is not allowed."
            />
          </Col>
        )}

        {loginErrorMessage && (
          <Col span={8} offset={8} className="gutter-row">
            <Alert type="error" showIcon title={loginErrorMessage} />
          </Col>
        )}

        <Col span={8} offset={8} className="gutter-row">
          <Form.Item
            name="email"
            rules={[
              {
                required: true,
              },
              {
                type: 'email',
              },
            ]}
          >
            <Input
              size="large"
              placeholder="Email"
              prefix={<UserOutlined />}
              type="email"
              disabled={userInProcess}
            />
          </Form.Item>
        </Col>

        <Col span={8} offset={8} className="gutter-row">
          <Form.Item
            name="password"
            rules={[
              {
                required: true,
              },
              {
                type: 'string',
                min: 8,
              },
            ]}
          >
            <Input
              size="large"
              placeholder="Password"
              prefix={<LockOutlined />}
              type="password"
              disabled={userInProcess}
            />
          </Form.Item>
        </Col>

        <Col span={8} offset={8} className="gutter-row">
          <Form.Item shouldUpdate>
            {(): React.ReactElement => (
              <Button
                type="primary"
                shape="round"
                htmlType="submit"
                icon={<LoginOutlined />}
                size="large"
                disabled={
                  !form.isFieldsTouched(true) ||
                  !!form.getFieldsError().filter(({ errors }) => errors.length)
                    .length ||
                  userInProcess
                }
              >
                Sign in
              </Button>
            )}
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
}

export default LoginContainer;
